import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useTenantNavigation } from "@/hooks/useTenantNavigation";
import { useAuth } from "@/contexts/AuthContext";
import { useOffline } from "@/contexts/OfflineContext";
import api from "@/lib/api";
import {
  cacheLookups, getCachedLookups, cacheShiftLogs, getCachedShiftLogs,
  cacheReport, getCachedReport, appendOfflineLog, removeOfflineLog,
  generateOfflineId, isOfflineId,
} from "@/lib/offlineStorage";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Trash2, Play, Square, Truck,
  AlertTriangle, Clock, Zap, Download, LogOut, Radio, RefreshCw, BarChart3, List, Gauge,
  Shield, ClipboardCheck, WifiOff, CloudUpload
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { NotificationBell } from "@/components/NotificationBell";

const EVENT_TYPES = [
  { value: "equipment_start", label: "Equipment Start", icon: Play, color: "text-emerald-400", bg: "bg-emerald-400/10 border-emerald-400/20" },
  { value: "equipment_end", label: "Equipment End", icon: Square, color: "text-red-400", bg: "bg-red-400/10 border-red-400/20" },
  { value: "material_trip", label: "Material Trip", icon: Truck, color: "text-amber-400", bg: "bg-amber-400/10 border-amber-400/20" },
  { value: "delay_start", label: "Delay Start", icon: AlertTriangle, color: "text-orange-400", bg: "bg-orange-400/10 border-orange-400/20" },
  { value: "delay_end", label: "Delay End", icon: Clock, color: "text-blue-400", bg: "bg-blue-400/10 border-blue-400/20" },
  { value: "safety_event", label: "Safety/Environment", icon: Shield, color: "text-red-400", bg: "bg-red-400/10 border-red-400/20" },
  { value: "quality_note", label: "Quality Note", icon: ClipboardCheck, color: "text-purple-400", bg: "bg-purple-400/10 border-purple-400/20" },
];

function getCurrentTime() {
  const now = new Date();
  return `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
}

function getEventMeta(type) {
  return EVENT_TYPES.find((e) => e.value === type) || EVENT_TYPES[0];
}

export default function QuickLogPage() {
  const { reportId } = useParams();
  const { user, logout } = useAuth();
  const { navigate } = useTenantNavigation();
  const { isOnline, enqueue, queueLength } = useOffline();
  const [logs, setLogs] = useState([]);
  const [lookups, setLookups] = useState({});
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [populating, setPopulating] = useState(false);
  const [liveMode, setLiveMode] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [viewMode, setViewMode] = useState("feed");
  const feedRef = useRef(null);
  const pollRef = useRef(null);
  const prevLogCount = useRef(0);

  const [form, setForm] = useState({
    event_type: "equipment_start",
    equipment_id: "",
    activity: "",
    time: getCurrentTime(),
    operator_name: "",
    material_id: "",
    from_location_id: "",
    to_location_id: "",
    quantity: "",
    loads: "",
    unit: "ton",
    delay_code_id: "",
    notes: "",
  });

  const loadData = useCallback(async (silent = false) => {
    try {
      const [logData, lookupData, reportData] = await Promise.all([
        api.get(`/reports/${reportId}/shift-logs`),
        api.get("/lookups"),
        api.get(`/reports/${reportId}`),
      ]);
      const newLogs = logData || [];
      if (!silent && newLogs.length > prevLogCount.current && prevLogCount.current > 0) {
        toast.info(`${newLogs.length - prevLogCount.current} new event(s) logged`);
      }
      prevLogCount.current = newLogs.length;
      setLogs(newLogs);
      setLookups(lookupData || {});
      setReport(reportData);
      setLastRefresh(new Date());
      // Cache for offline use
      cacheShiftLogs(reportId, newLogs);
      cacheLookups(lookupData);
      cacheReport(reportId, reportData);
    } catch {
      // Attempt offline fallback
      const [cachedLogs, cachedLookups, cachedReport] = await Promise.all([
        getCachedShiftLogs(reportId),
        getCachedLookups(),
        getCachedReport(reportId),
      ]);
      if (cachedLookups || cachedReport) {
        setLogs(cachedLogs || []);
        setLookups(cachedLookups || {});
        setReport(cachedReport);
        prevLogCount.current = (cachedLogs || []).length;
        if (!silent) toast.info("Loaded from offline cache");
      } else {
        if (!silent) toast.error("No connection and no cached data available");
      }
    } finally {
      setLoading(false);
    }
  }, [reportId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Auto-refresh polling every 10s when live mode is on and online
  useEffect(() => {
    if (liveMode && !dialogOpen && isOnline) {
      pollRef.current = setInterval(() => loadData(true), 10000);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [liveMode, dialogOpen, loadData, isOnline]);

  useEffect(() => {
    if (feedRef.current) {
      feedRef.current.scrollTop = feedRef.current.scrollHeight;
    }
  }, [logs]);

  const openDialog = (eventType) => {
    // Pre-load common fields from most recent logs
    let preloadEquipId = "";
    let preloadOperator = user?.name || "";
    let preloadMaterial = "";
    let preloadFrom = "";
    let preloadTo = "";
    let preloadUnit = "ton";
    let preloadDelayCode = "";
    let preloadActivity = "";

    if (logs.length > 0) {
      // Find the most recent active equipment (started but not ended)
      const starts = logs.filter((l) => l.event_type === "equipment_start");
      const ends = logs.filter((l) => l.event_type === "equipment_end");
      const endedEquipIds = new Set(ends.map((e) => e.equipment_id));
      const activeStart = [...starts].reverse().find((s) => !endedEquipIds.has(s.equipment_id));

      if (activeStart) {
        preloadEquipId = activeStart.equipment_id;
        preloadOperator = activeStart.operator_name || preloadOperator;
        preloadActivity = activeStart.activity || "";
      } else if (starts.length > 0) {
        preloadEquipId = starts[starts.length - 1].equipment_id;
        preloadOperator = starts[starts.length - 1].operator_name || preloadOperator;
        preloadActivity = starts[starts.length - 1].activity || "";
      }

      // Pre-load material trip fields from most recent trip
      const lastTrip = [...logs].reverse().find((l) => l.event_type === "material_trip");
      if (lastTrip) {
        preloadMaterial = lastTrip.material_id || "";
        preloadFrom = lastTrip.from_location_id || "";
        preloadTo = lastTrip.to_location_id || "";
        preloadUnit = lastTrip.unit || "ton";
      }

      // Pre-load delay code from most recent delay
      const lastDelay = [...logs].reverse().find((l) => l.event_type === "delay_start");
      if (lastDelay) {
        preloadDelayCode = lastDelay.delay_code_id || "";
      }
    }

    const isFollowUp = eventType !== "equipment_start";

    setForm({
      event_type: eventType || "equipment_start",
      equipment_id: isFollowUp ? preloadEquipId : "",
      activity: eventType === "equipment_start" ? preloadActivity : "",
      time: getCurrentTime(),
      operator_name: preloadOperator,
      material_id: eventType === "material_trip" ? preloadMaterial : "",
      from_location_id: eventType === "material_trip" ? preloadFrom : "",
      to_location_id: eventType === "material_trip" ? preloadTo : "",
      quantity: "",
      loads: "",
      unit: preloadUnit,
      delay_code_id: eventType === "delay_start" ? preloadDelayCode : "",
      notes: "",
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const noEquipNeeded = ["safety_event", "quality_note"];
    if (!noEquipNeeded.includes(form.event_type) && !form.equipment_id) {
      toast.error("Select equipment");
      return;
    }
    if (form.event_type === "material_trip" && (form.loads === "" || form.loads === null || parseInt(form.loads) < 1)) {
      toast.error("Loads is required (minimum 1)");
      return;
    }
    setSaving(true);
    try {
      if (isOnline) {
        await api.post(`/reports/${reportId}/shift-logs`, form);
        toast.success("Event logged");
        setDialogOpen(false);
        await loadData();
      } else {
        // Offline: save locally and queue for sync
        const offlineLog = {
          ...form,
          id: generateOfflineId(),
          report_id: reportId,
          created_at: new Date().toISOString(),
          _offline: true,
        };
        const updatedLogs = await appendOfflineLog(reportId, offlineLog);
        setLogs(updatedLogs);
        prevLogCount.current = updatedLogs.length;
        await enqueue({
          id: generateOfflineId(),
          type: "create_shift_log",
          reportId,
          payload: form,
        });
        toast.success("Event saved offline — will sync when connected");
        setDialogOpen(false);
      }
    } catch {
      toast.error("Failed to log event");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (logId) => {
    try {
      if (isOfflineId(logId)) {
        // Remove from local offline storage
        const updatedLogs = await removeOfflineLog(reportId, logId);
        setLogs(updatedLogs);
        toast.success("Offline log removed");
      } else if (isOnline) {
        await api.delete(`/reports/${reportId}/shift-logs/${logId}`);
        toast.success("Log removed");
        await loadData();
      } else {
        // Queue the delete for sync
        await enqueue({
          id: generateOfflineId(),
          type: "delete_shift_log",
          reportId,
          logId,
        });
        setLogs((prev) => prev.filter((l) => l.id !== logId));
        toast.success("Delete queued — will sync when connected");
      }
    } catch {
      toast.error("Failed to delete");
    }
  };

  const handlePopulate = async () => {
    if (logs.length === 0) {
      toast.error("No shift logs to populate");
      return;
    }
    setPopulating(true);
    try {
      const result = await api.post(`/reports/${reportId}/shift-logs/populate`);
      const c = result.created;
      const parts = [];
      if (c.equipment_hours) parts.push(`${c.equipment_hours} equipment`);
      if (c.material_movements) parts.push(`${c.material_movements} material`);
      if (c.delay_events) parts.push(`${c.delay_events} delay`);
      if (c.safety_events) parts.push(`${c.safety_events} safety`);
      if (c.quality_notes) parts.push(`${c.quality_notes} quality`);
      toast.success(`Created ${parts.join(", ")} entries`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to populate");
    } finally {
      setPopulating(false);
    }
  };

  const equipName = (id) => {
    const e = lookups.equipment?.find((eq) => eq.id === id);
    return e ? `${e.unit_id} (${e.equipment_type})` : id || "—";
  };
  const matName = (id) => lookups.materials?.find((m) => m.id === id)?.name || id || "";
  const locName = (id) => lookups.locations?.find((l) => l.id === id)?.name || id || "";
  const delayName = (id) => {
    const dc = lookups.delay_codes?.find((d) => d.id === id);
    return dc ? `${dc.code} - ${dc.description}` : id || "";
  };
  const siteName = lookups.sites?.find((s) => s.id === report?.site_id)?.name || "";

  const isReadOnly = report?.status === "Submitted";

  // Shift Replay: build timeline blocks from paired start/end events
  const timelineBlocks = useMemo(() => {
    const byEquip = {};
    logs.forEach((log) => {
      const eid = log.equipment_id;
      if (!eid) return;
      if (!byEquip[eid]) byEquip[eid] = { id: eid, name: equipName(eid), events: [] };
      byEquip[eid].events.push(log);
    });

    return Object.values(byEquip).map((eq) => {
      const blocks = [];
      const starts = eq.events.filter((e) => e.event_type === "equipment_start");
      const ends = eq.events.filter((e) => e.event_type === "equipment_end");
      const dStarts = eq.events.filter((e) => e.event_type === "delay_start");
      const dEnds = eq.events.filter((e) => e.event_type === "delay_end");
      const trips = eq.events.filter((e) => e.event_type === "material_trip");

      // Equipment running blocks
      starts.forEach((s, i) => {
        const e = ends[i];
        blocks.push({
          type: "equipment",
          start: s.time,
          end: e?.time || null,
          label: "Running",
          operator: s.operator_name,
          color: "bg-emerald-500/60 border-emerald-500",
        });
      });

      // Delay blocks
      dStarts.forEach((s, i) => {
        const e = dEnds[i];
        blocks.push({
          type: "delay",
          start: s.time,
          end: e?.time || null,
          label: s.delay_code_id ? delayName(s.delay_code_id) : "Delay",
          color: "bg-orange-500/60 border-orange-500",
        });
      });

      // Material trip markers
      trips.forEach((t) => {
        blocks.push({
          type: "trip",
          start: t.time,
          end: t.time,
          label: matName(t.material_id) || "Trip",
          detail: `${locName(t.from_location_id)} → ${locName(t.to_location_id)}`,
          color: "bg-amber-500/60 border-amber-500",
        });
      });

      blocks.sort((a, b) => (a.start || "").localeCompare(b.start || ""));
      return { ...eq, blocks };
    });
  }, [logs, lookups]);

  // Determine visible time range from logs
  const timeRange = useMemo(() => {
    const times = logs.map((l) => l.time).filter(Boolean);
    if (times.length === 0) return { min: 6, max: 18 };
    const mins = times.map((t) => { const [h, m] = t.split(":").map(Number); return h * 60 + m; });
    const lo = Math.min(...mins);
    const hi = Math.max(...mins);
    const padH = Math.max(0, Math.floor(lo / 60) - 1);
    const padE = Math.min(24, Math.ceil(hi / 60) + 1);
    return { min: padH, max: padE };
  }, [logs]);

  const getPos = (time) => {
    if (!time) return 0;
    const [h, m] = time.split(":").map(Number);
    const totalMins = h * 60 + m;
    const rangeMins = (timeRange.max - timeRange.min) * 60;
    return ((totalMins - timeRange.min * 60) / rangeMins) * 100;
  };

  const getWidth = (start, end) => {
    if (!start || !end) return 0;
    return Math.max(getPos(end) - getPos(start), 1.5);
  };

  // Shift Summary: auto-calculate metrics from logs
  const shiftSummary = useMemo(() => {
    const byEquip = {};
    logs.forEach((log) => {
      const eid = log.equipment_id;
      if (!eid) return;
      if (!byEquip[eid]) byEquip[eid] = [];
      byEquip[eid].push(log);
    });

    let totalEquipMins = 0;
    let totalDelayMins = 0;
    let tripCount = 0;
    let totalLoads = 0;
    let totalQty = 0;
    const equipCount = Object.keys(byEquip).length;

    const timeDiffMins = (start, end) => {
      if (!start || !end) return 0;
      const [sh, sm] = start.split(":").map(Number);
      const [eh, em] = end.split(":").map(Number);
      let diff = (eh * 60 + em) - (sh * 60 + sm);
      if (diff < 0) diff += 1440;
      return diff;
    };

    Object.values(byEquip).forEach((events) => {
      const starts = events.filter((e) => e.event_type === "equipment_start");
      const ends = events.filter((e) => e.event_type === "equipment_end");
      const dStarts = events.filter((e) => e.event_type === "delay_start");
      const dEnds = events.filter((e) => e.event_type === "delay_end");
      const trips = events.filter((e) => e.event_type === "material_trip");

      starts.forEach((s, i) => {
        const e = ends[i];
        if (e) totalEquipMins += timeDiffMins(s.time, e.time);
      });
      dStarts.forEach((s, i) => {
        const e = dEnds[i];
        if (e) totalDelayMins += timeDiffMins(s.time, e.time);
      });
      tripCount += trips.length;
      trips.forEach((t) => {
        totalLoads += Number(t.loads) || 0;
        totalQty += Number(t.quantity) || 0;
      });
    });

    const equipHours = Math.round((totalEquipMins / 60) * 100) / 100;
    const delayHours = Math.round((totalDelayMins / 60) * 100) / 100;
    const productiveHours = Math.round(((totalEquipMins - totalDelayMins) / 60) * 100) / 100;
    const utilization = totalEquipMins > 0 ? Math.round(((totalEquipMins - totalDelayMins) / totalEquipMins) * 100) : 0;

    return { equipHours, delayHours, productiveHours, utilization, tripCount, totalLoads, totalQty, equipCount };
  }, [logs]);

  // Filter locations based on report site
  const siteLocations = (lookups.locations || []).filter(
    (l) => !l.site_id || l.site_id === report?.site_id
  );

  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading...</div>;

  const eventType = form.event_type;
  const needsMaterial = eventType === "material_trip";
  const needsDelay = eventType === "delay_start";
  const needsEquipment = !["safety_event", "quality_note"].includes(eventType);
  const isSafetyOrQuality = ["safety_event", "quality_note"].includes(eventType);

  return (
    <div className="min-h-screen flex flex-col" data-testid="quick-log-page">
      {/* Header */}
      <header className="border-b border-border px-4 sm:px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Logo />
        </div>
        <div className="flex items-center gap-4">
          <NotificationBell />
          <span className="text-sm text-muted-foreground hidden sm:block">{user?.name || user?.email}</span>
          <Button variant="ghost" size="icon" onClick={logout} data-testid="logout-button">
            <LogOut className="h-4 w-4" strokeWidth={1.5} />
          </Button>
        </div>
      </header>

      <main className="flex-1 max-w-2xl mx-auto w-full px-4 py-6 flex flex-col">
        {/* Nav + Title */}
        <Button variant="ghost" size="sm" className="mb-4 text-muted-foreground self-start" onClick={() => navigate(`/report/${reportId}/dashboard`)} data-testid="back-to-dashboard">
          <ArrowLeft className="h-4 w-4 mr-1" strokeWidth={1.5} /> Dashboard
        </Button>

        <div className="flex items-center justify-between mb-2">
          <div>
            <h1 className="font-headline text-xl sm:text-2xl font-bold tracking-tight flex items-center gap-2">
              <Zap className="h-5 w-5 text-amber-400" strokeWidth={2} />
              Quick-Log
            </h1>
            <p className="text-muted-foreground text-xs sm:text-sm mt-1">
              {report?.report_date} | {siteName} | {report?.shift} Shift
            </p>
          </div>
          <Badge variant="outline" className="text-xs">
            {logs.length} events
          </Badge>
        </div>

        {/* Quick-action buttons row */}
        {!isReadOnly && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 my-4" data-testid="quick-action-grid">
            {EVENT_TYPES.map((et) => (
              <button
                key={et.value}
                onClick={() => openDialog(et.value)}
                className={`flex items-center gap-2 px-3 py-2.5 rounded-md border text-sm font-medium transition-all hover:scale-[1.02] active:scale-[0.98] ${et.bg}`}
                data-testid={`quick-btn-${et.value}`}
              >
                <et.icon className={`h-4 w-4 ${et.color}`} strokeWidth={2} />
                <span className="truncate">{et.label}</span>
              </button>
            ))}
          </div>
        )}

        {/* Shift Summary Card */}
        {logs.length > 0 && (
          <Card className="border-border bg-card mb-3 mt-1" data-testid="shift-summary-card">
            <CardContent className="py-3 px-4">
              <div className="flex items-center gap-2 mb-3">
                <Gauge className="h-4 w-4 text-primary" strokeWidth={2} />
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Shift Summary</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div data-testid="summary-equip-hours">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Equipment</p>
                  <p className="font-mono text-lg font-bold text-emerald-400">{shiftSummary.equipHours}<span className="text-xs font-normal text-muted-foreground ml-0.5">h</span></p>
                </div>
                <div data-testid="summary-delay-hours">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Delays</p>
                  <p className="font-mono text-lg font-bold text-orange-400">{shiftSummary.delayHours}<span className="text-xs font-normal text-muted-foreground ml-0.5">h</span></p>
                </div>
                <div data-testid="summary-trips">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Trips</p>
                  <p className="font-mono text-lg font-bold text-amber-400">{shiftSummary.tripCount}
                    {shiftSummary.totalLoads > 0 && <span className="text-xs font-normal text-muted-foreground ml-1">({shiftSummary.totalLoads} loads)</span>}
                  </p>
                </div>
                <div data-testid="summary-utilization">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Utilization</p>
                  <p className={`font-mono text-lg font-bold ${shiftSummary.utilization >= 80 ? "text-emerald-400" : shiftSummary.utilization >= 50 ? "text-amber-400" : "text-red-400"}`}>
                    {shiftSummary.utilization}<span className="text-xs font-normal text-muted-foreground ml-0.5">%</span>
                  </p>
                </div>
              </div>
              {/* Utilization bar */}
              <div className="mt-3 h-1.5 bg-background rounded-full overflow-hidden" data-testid="utilization-bar">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${shiftSummary.utilization >= 80 ? "bg-emerald-500" : shiftSummary.utilization >= 50 ? "bg-amber-500" : "bg-red-500"}`}
                  style={{ width: `${Math.min(shiftSummary.utilization, 100)}%` }}
                />
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-[10px] text-muted-foreground">{shiftSummary.equipCount} equipment | {shiftSummary.productiveHours}h productive</span>
                {shiftSummary.totalQty > 0 && <span className="text-[10px] text-muted-foreground">{shiftSummary.totalQty} total qty</span>}
              </div>
            </CardContent>
          </Card>
        )}

        {/* View Toggle + Controls */}
        <div className="flex items-center justify-between mt-2 mb-3">
          <div className="flex items-center gap-2">
            <div className="flex items-center bg-card border border-border rounded-md p-0.5" data-testid="view-toggle">
              <button
                onClick={() => setViewMode("feed")}
                className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded transition-colors ${viewMode === "feed" ? "bg-primary/20 text-primary" : "text-muted-foreground"}`}
                data-testid="view-feed-btn"
              >
                <List className="h-3 w-3" /> Feed
              </button>
              <button
                onClick={() => setViewMode("timeline")}
                className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded transition-colors ${viewMode === "timeline" ? "bg-primary/20 text-primary" : "text-muted-foreground"}`}
                data-testid="view-timeline-btn"
              >
                <BarChart3 className="h-3 w-3" /> Shift Replay
              </button>
            </div>
            {viewMode === "feed" && (
              <button
                onClick={() => setLiveMode((v) => !v)}
                className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded-full border transition-colors ${
                  liveMode
                    ? "border-emerald-500/30 text-emerald-400 bg-emerald-500/10"
                    : "border-border text-muted-foreground"
                }`}
                data-testid="live-toggle"
              >
                <Radio className={`h-3 w-3 ${liveMode ? "animate-pulse" : ""}`} />
                {liveMode ? "LIVE" : "Paused"}
              </button>
            )}
            {!isOnline && (
              <span className="flex items-center gap-1.5 text-xs px-2 py-1 rounded-full border border-orange-500/30 text-orange-400 bg-orange-500/10" data-testid="offline-indicator">
                <WifiOff className="h-3 w-3" />
                Offline{queueLength > 0 && ` (${queueLength})`}
              </span>
            )}
            {lastRefresh && viewMode === "feed" && (
              <span className="text-[10px] text-muted-foreground/50 hidden sm:block">
                {lastRefresh.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-muted-foreground"
              onClick={() => loadData(false)}
              data-testid="refresh-feed-btn"
            >
              <RefreshCw className="h-3.5 w-3.5" />
            </Button>
            {!isReadOnly && logs.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                className="text-xs border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
                onClick={handlePopulate}
                disabled={populating}
                data-testid="populate-report-btn"
              >
                <Download className="h-3.5 w-3.5 mr-1" />
                {populating ? "Processing..." : "Populate Report"}
              </Button>
            )}
          </div>
        </div>

        {/* === FEED VIEW === */}
        {viewMode === "feed" && (
          <div
            ref={feedRef}
            className="flex-1 overflow-y-auto space-y-2 min-h-[200px] max-h-[55vh] pr-1"
            data-testid="activity-feed"
          >
            {logs.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                <Zap className="h-10 w-10 mb-3 opacity-30" />
                <p className="text-sm">No events logged yet</p>
                <p className="text-xs mt-1">Tap an event button above to start logging</p>
              </div>
            ) : (
              logs.map((log) => {
                const meta = getEventMeta(log.event_type);
                const Icon = meta.icon;
                return (
                  <Card key={log.id} className="border-border bg-card" data-testid={`log-entry-${log.id}`}>
                    <CardContent className="py-3 px-4 flex items-start gap-3">
                      <div className={`mt-0.5 p-1.5 rounded ${meta.bg}`}>
                        <Icon className={`h-3.5 w-3.5 ${meta.color}`} strokeWidth={2} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">{meta.label}</span>
                          <span className="font-mono text-xs text-muted-foreground">{log.time || "—"}</span>
                          {log._offline && (
                            <span className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-orange-500/10 border border-orange-500/20 text-orange-400">
                              <CloudUpload className="h-2.5 w-2.5" />Queued
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {equipName(log.equipment_id)}
                          {log.operator_name && ` — ${log.operator_name}`}
                          {log.event_type === "equipment_start" && log.activity && ` — ${log.activity}`}
                        </p>
                        {log.event_type === "material_trip" && (
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {matName(log.material_id)} | {locName(log.from_location_id)} → {locName(log.to_location_id)}
                            {log.quantity ? ` | ${log.quantity} ${log.unit || "ton"}` : ""}
                            {log.loads ? ` | ${log.loads} loads` : ""}
                          </p>
                        )}
                        {log.event_type === "delay_start" && log.delay_code_id && (
                          <p className="text-xs text-muted-foreground mt-0.5">
                            Reason: {delayName(log.delay_code_id)}
                          </p>
                        )}
                        {log.notes && <p className="text-xs text-muted-foreground/70 mt-0.5 italic">{log.notes}</p>}
                      </div>
                      {!isReadOnly && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-muted-foreground hover:text-red-400"
                          onClick={() => handleDelete(log.id)}
                          data-testid={`delete-log-${log.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        )}

        {/* === SHIFT REPLAY TIMELINE VIEW === */}
        {viewMode === "timeline" && (
          <Card className="border-border bg-card flex-1" data-testid="shift-replay-timeline">
            <CardContent className="py-4 px-4 sm:px-6">
              {logs.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                  <BarChart3 className="h-10 w-10 mb-3 opacity-30" />
                  <p className="text-sm">No events to replay</p>
                  <p className="text-xs mt-1">Log some events to see the shift timeline</p>
                </div>
              ) : (
                <>
                  <div className="mb-4">
                    <h3 className="font-headline text-sm font-semibold">Shift Replay</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">How the shift unfolded, minute by minute</p>
                  </div>

                  {/* Time axis */}
                  <div className="relative mb-2 border-b border-border pb-2 h-5">
                    <div className="flex text-[10px] text-muted-foreground font-mono">
                      {Array.from({ length: timeRange.max - timeRange.min + 1 }, (_, i) => timeRange.min + i).map((h) => (
                        <span
                          key={h}
                          className="absolute"
                          style={{ left: `${((h - timeRange.min) / (timeRange.max - timeRange.min)) * 100}%`, transform: "translateX(-50%)" }}
                        >
                          {String(h).padStart(2, "0")}:00
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Equipment rows */}
                  <div className="space-y-3 mt-4">
                    {timelineBlocks.map((eq) => (
                      <div key={eq.id} className="relative">
                        <div className="flex items-center gap-3">
                          <div className="w-24 sm:w-32 text-xs font-medium truncate shrink-0" title={eq.name}>
                            {eq.name}
                          </div>
                          <div className="flex-1 relative h-9 bg-background rounded border border-border">
                            {/* Now marker (subtle vertical line) */}
                            {(() => {
                              const nowPos = getPos(getCurrentTime());
                              return nowPos > 0 && nowPos < 100 ? (
                                <div
                                  className="absolute top-0 bottom-0 w-px bg-white/20 z-10"
                                  style={{ left: `${nowPos}%` }}
                                />
                              ) : null;
                            })()}

                            {eq.blocks.map((block, idx) => {
                              const isTrip = block.type === "trip";
                              const left = getPos(block.start);
                              const width = isTrip ? 2 : getWidth(block.start, block.end);

                              const tooltip = isTrip
                                ? `${block.start} — ${block.label}: ${block.detail || ""}`
                                : `${block.start}–${block.end || "ongoing"}: ${block.label}${block.operator ? ` (${block.operator})` : ""}`;

                              if (isTrip) {
                                // Diamond marker for material trips
                                return (
                                  <div
                                    key={idx}
                                    className="absolute top-1/2 -translate-y-1/2 z-20"
                                    style={{ left: `${left}%` }}
                                    title={tooltip}
                                  >
                                    <div className="w-3 h-3 bg-amber-500 border border-amber-400 rotate-45 -ml-1.5 cursor-pointer hover:scale-125 transition-transform" />
                                  </div>
                                );
                              }

                              // Duration block for equipment / delays
                              return (
                                <div
                                  key={idx}
                                  className={`absolute top-1 bottom-1 rounded border ${block.color} cursor-pointer hover:opacity-80 transition-opacity flex items-center justify-center overflow-hidden`}
                                  style={{ left: `${left}%`, width: `${Math.max(width, 2)}%` }}
                                  title={tooltip}
                                >
                                  {width > 10 && (
                                    <span className="text-[9px] text-white font-medium truncate px-1">
                                      {block.label}
                                    </span>
                                  )}
                                </div>
                              );
                            })}

                            {/* Ongoing indicator if equipment started but not ended */}
                            {eq.blocks.some((b) => b.type === "equipment" && !b.end) && (
                              <div
                                className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-1"
                              >
                                <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Legend */}
                  <div className="flex flex-wrap items-center gap-4 mt-6 pt-4 border-t border-border">
                    <span className="text-xs text-muted-foreground">Legend:</span>
                    <div className="flex items-center gap-1.5">
                      <div className="w-3 h-2 rounded bg-emerald-500/60 border border-emerald-500" />
                      <span className="text-xs">Running</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="w-3 h-2 rounded bg-orange-500/60 border border-orange-500" />
                      <span className="text-xs">Delay</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="w-3 h-3 bg-amber-500 border border-amber-400 rotate-45" />
                      <span className="text-xs ml-0.5">Material Trip</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                      <span className="text-xs">Active</span>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* Floating add button for mobile */}
        {!isReadOnly && (
          <button
            onClick={() => openDialog("equipment_start")}
            className="fixed bottom-6 right-6 sm:hidden h-14 w-14 rounded-full bg-primary text-primary-foreground shadow-lg flex items-center justify-center hover:bg-primary/90 active:scale-95 transition-transform z-50"
            data-testid="fab-log-event"
          >
            <Plus className="h-6 w-6" />
          </button>
        )}
      </main>

      {/* Log Event Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="log-event-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="h-4 w-4 text-amber-400" />
              Log Event
            </DialogTitle>
            <DialogDescription>Record a shift event with auto-timestamp</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Event Type */}
            <div>
              <Label>Event Type</Label>
              <Select value={form.event_type} onValueChange={(v) => setForm({ ...form, event_type: v })}>
                <SelectTrigger data-testid="select-event-type"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  {EVENT_TYPES.map((et) => (
                    <SelectItem key={et.value} value={et.value}>{et.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Equipment - hidden for safety/quality events */}
            {needsEquipment && (
              <div>
                <Label>Equipment</Label>
                <Select value={form.equipment_id} onValueChange={(v) => setForm({ ...form, equipment_id: v })}>
                  <SelectTrigger data-testid="select-equipment"><SelectValue placeholder="Select equipment" /></SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {(lookups.equipment || []).map((e) => (
                      <SelectItem key={e.id} value={e.id}>{e.unit_id} ({e.equipment_type})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Activity - only for Equipment Start */}
            {eventType === "equipment_start" && (
              <div>
                <Label>Activity</Label>
                <Select value={form.activity} onValueChange={(v) => setForm({ ...form, activity: v })}>
                  <SelectTrigger data-testid="select-activity"><SelectValue placeholder="Select activity" /></SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {((lookups.activities || []).length ? lookups.activities.map(a => a.name) : ["Stripping", "Reclamation", "Mining/Production", "Mine Maintenance/Development", "Pond Maintenance", "Plant Maintenance"]).map((a) => (
                      <SelectItem key={a} value={a}>{a}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Time */}
            <div>
              <Label>Time</Label>
              <Input
                type="time"
                value={form.time}
                onChange={(e) => setForm({ ...form, time: e.target.value })}
                data-testid="input-time"
              />
            </div>

            {/* Operator - hidden for safety/quality events */}
            {needsEquipment && (
              <div>
                <Label>Operator Name</Label>
                <Input
                  value={form.operator_name}
                  onChange={(e) => setForm({ ...form, operator_name: e.target.value })}
                  placeholder="Operator"
                  data-testid="input-operator"
                />
              </div>
            )}

            {/* Material Trip fields */}
            {needsMaterial && (
              <>
                <div>
                  <Label>Material</Label>
                  <Select value={form.material_id} onValueChange={(v) => setForm({ ...form, material_id: v })}>
                    <SelectTrigger data-testid="select-material"><SelectValue placeholder="Select material" /></SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {(lookups.materials || []).filter((m) => !m.site_id || m.site_id === report?.site_id).map((m) => (
                        <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>From</Label>
                    <Select value={form.from_location_id} onValueChange={(v) => setForm({ ...form, from_location_id: v })}>
                      <SelectTrigger data-testid="select-from-loc"><SelectValue placeholder="Origin" /></SelectTrigger>
                      <SelectContent className="bg-card border-border">
                        {siteLocations.map((l) => (
                          <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>To</Label>
                    <Select value={form.to_location_id} onValueChange={(v) => setForm({ ...form, to_location_id: v })}>
                      <SelectTrigger data-testid="select-to-loc"><SelectValue placeholder="Destination" /></SelectTrigger>
                      <SelectContent className="bg-card border-border">
                        {siteLocations.map((l) => (
                          <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label>Quantity</Label>
                    <Input
                      type="number"
                      value={form.quantity}
                      onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                      placeholder="0"
                      data-testid="input-quantity"
                    />
                  </div>
                  <div>
                    <Label>Loads *</Label>
                    <Input
                      type="number"
                      min="1"
                      step="1"
                      value={form.loads}
                      onChange={(e) => setForm({ ...form, loads: e.target.value })}
                      placeholder="1"
                      data-testid="input-loads"
                    />
                  </div>
                  <div>
                    <Label>Unit</Label>
                    <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
                      <SelectTrigger data-testid="select-unit"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-card border-border">
                        <SelectItem value="ton">Ton</SelectItem>
                        <SelectItem value="cyd">CYD</SelectItem>
                        <SelectItem value="lcy">LCY</SelectItem>
                        <SelectItem value="bcy">BCY</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </>
            )}

            {/* Delay fields */}
            {needsDelay && (
              <div>
                <Label>Delay Code</Label>
                <Select value={form.delay_code_id} onValueChange={(v) => setForm({ ...form, delay_code_id: v })}>
                  <SelectTrigger data-testid="select-delay-code"><SelectValue placeholder="Select delay reason" /></SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {(lookups.delay_codes || []).map((d) => (
                      <SelectItem key={d.id} value={d.id}>{d.code} - {d.description}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Notes */}
            <div>
              <Label>{isSafetyOrQuality ? "Details" : "Notes (optional)"}</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder={
                  eventType === "safety_event" ? "Describe the safety/environmental observation..."
                  : eventType === "quality_note" ? "Describe the quality observation..."
                  : "Additional details..."
                }
                rows={isSafetyOrQuality ? 3 : 2}
                data-testid="input-notes"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-log-btn">
              {saving ? "Saving..." : "Log Event"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
